import {
  ClearRounded,
  EditRounded,
  HorizontalSplitRounded,
} from "@mui/icons-material";
import { Avatar, IconButton, Tooltip } from "@mui/material";
import React from "react";
import { useEffect } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Dialog } from "../../layout";
import { useContext } from "react";
import { AppContext } from "../../App";
import BarcodeGen from "./BarcodeGen";
import { useTranslation } from "react-i18next";
import UplProdList from "./UplProdList";
import { Button } from "../../controls";
import { action, controller, Server } from "../../api/Server";
import DynamicTable from "../../layout/DynamicTable/DynamicTable";
import { no_img } from "../../assets";

const Products = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [tblData, setTblData] = useState([]);

  const [showCsvUpl, setShowCsvUpl] = useState(false);
  const navigate = useNavigate();
  const { settings, authUser, setPageTitle } = useContext(AppContext);
  const [barCode, setBarCode] = useState("abcd");
  const [showBarcode, setShowBarcode] = useState(false);
  const [access, setAccess] = useState({});
  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });

  useEffect(() => {
    setPageTitle("Products");
    if (authUser && authUser.featuresAccess && authUser.featuresAccess.product)
      setAccess({ ...authUser.featuresAccess.product });
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);

  const handleDlt = (id) => {
    access.isHaveDeleteAccess &&
      setAlert({
        isOpen: true,
        type: "confirmation",
        title: t("areYouSure"),
        subTitle: t("prodDltTitle"),
        onConfirm: () => {
          const onComplete = (res) => {
            if (res.status === 200) {
              setAlert({
                isOpen: true,
                type: "success",
                title: t("dltSuccessTitle"),
                subTitle: res.msg,
              });
              setProducts((x) => [...x.filter((j) => j.id !== res.result.id)]);
            } else
              setAlert({
                isOpen: true,
                type: "error",
                title: t("error"),
                subTitle: res.msg,
              });
          };
          // Call delete on the server.
          Server(controller.Product).Delete(id, onComplete);
        },
      });
  };

  useEffect(() => {
    setTblData([
      ...products.map((prod) => {
        return {
          ...prod,
          prodInfo: (
            <div className="d-flex g-10">
              <Avatar
                src={prod.imgUrl ? prod.imgUrl : no_img}
                alt="Product"
                variant="rounded"
                sx={{ width: 45, height: 45 }}
              />
              <div className="d-grid g-2">
                <span>{prod.name}</span>
                <span className="fs-12 fw-5">{prod.barcode}</span>
              </div>
            </div>
          ),
          catInfo: (
            <div className="d-grid g-2">
              <span>{prod.catName}</span>{" "}
              {prod.subcatName && (
                <span>
                  {" "}
                  {" > "} {prod.subcatName}
                </span>
              )}
            </div>
          ),
          inv: <span className="fw-5 gray-color fs-18">{prod.inventory}</span>,
          prodPrice: (
            <div className="d-grid g-2 fs-14">
              <div className="d-flex g-5">
                <span>{t("purchasePrice")}: </span>
                <span className="fw-5">
                  {settings.curSymbol} {prod.purchPrice}
                </span>
              </div>
              <div className="d-flex g-5">
                <span>{t("sellPrice")}: </span>
                <span className="fw-5">
                  {settings.curSymbol} {prod.sellPrice}
                </span>
              </div>
            </div>
          ),
        };
      }),
    ]);
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  const handleBarcode = (barcode) => {
    setBarCode(barcode);
    setShowBarcode(true);
  };

  const handleImportExcel = () => {
    setShowCsvUpl(true);
  };

  const ActionComp = (props) => (
    <div className="bs-secondary br-8 w-fit">
      {access.isHaveBarcodePrintAccess && (
        <Tooltip arrow title={t("printBarcode")}>
          <IconButton
            onClick={() => handleBarcode(props.barcode)}
            size="small"
            color="primary"
            className="mr-5"
          >
            <HorizontalSplitRounded className="fs-18 m-2" />
          </IconButton>
        </Tooltip>
      )}
      {access.isHaveEditAccess && (
        <Tooltip arrow title={t("edit")}>
          <IconButton
            onClick={() => navigate(`product-form/${props.id}`)}
            size="small"
            color="primary"
            className="mr-5"
          >
            <EditRounded className="fs-18 m-2" />
          </IconButton>
        </Tooltip>
      )}
      {access.isHaveDeleteAccess && (
        <Tooltip arrow title={t("delete")}>
          <IconButton
            onClick={() => handleDlt(props.id)}
            size="small"
            color="error"
          >
            <ClearRounded className="fs-18 m-2" />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );

  const HeaderComp = () => (
    <div className="d-flex flex-wrap g-8 w-100pr">
      {access.isHaveAddAccess && (
        <Button
          onClick={() => navigate("product-form")}
          variant="text"
          size="small"
          sx={{
            width: { md: "180px", xs: "100%" },
            fontSize: "17px",
            padding: "8px",
          }}
        >
          {t("addProduct")}
        </Button>
      )}
      {access.isHaveImportAccess && (
        <Button
          variant="text"
          sx={{ fontSize: "17px", width: { md: "180px", xs: "100%" } }}
          onClick={handleImportExcel}
        >
          {t("importExcel")}
        </Button>
      )}
    </div>
  );

  return (
    <>
      <DynamicTable
        records={tblData}
        setRecords={setProducts}
        endpointKey={controller.Product + action.GetAllByOrgPg}
        exportEndpoint={controller.Product + action.ExportProducts}
        ActionComp={ActionComp}
        HeaderComp={HeaderComp}
        enableExport={access.isHaveExportAccess}
        headers={[
          { id: "actions", label: t("actions") },
          { id: "prodInfo", label: t("product") },
          { id: "catInfo", label: t("category") },
          { id: "inv", label: t("inventory") },
          { id: "prodPrice", label: t("price") },
        ]}
      />

      {/* Alert  */}
      <Alert alert={alert} setAlert={setAlert} />

      {/* Generate Barcode */}
      <Dialog show={showBarcode} onClose={false} maxWidth="lg">
        <BarcodeGen value={barCode} setShowPopup={setShowBarcode} />
      </Dialog>

      {/* Upload File Form */}
      {showCsvUpl && (
        <Dialog show={showCsvUpl} onClose={false} maxWidth="md">
          <UplProdList
            {...{
              setShowForm: setShowCsvUpl,
              setAlert,
              alert,
            }}
          />
        </Dialog>
      )}
    </>
  );
};

export default Products;
